import { useState } from 'react';
import { Contours } from '../ui/Contours';
import { historyStore, useHistoryEntries, type HistoryEntry } from '../state/history';
import { formatClock } from '../util/format';

// v14 BOLT-049 — the device-local match history: every finished round on this
// device (no accounts), each with its GIF replay + the cached event timeline.
// A pruned server replay shows an honest "expired" state; the timeline was
// cached at write time so it survives.

const KIND_LABEL: Record<string, string> = {
  headstart: 'Head start began',
  card: 'Question played',
  pending: 'Question pending',
  veto: 'VETOED',
  curse: 'Card cast',
  bonus: 'Bonus banked',
  portal_armed: 'Portal armed',
  portal_triggered: 'Portal triggered',
  portal_cleared: 'Portal cleared',
  photo_open: 'Photo window opened',
  photo: 'Photo delivered',
  photo_fail: 'Photo missed',
  thermo_open: 'Slice walk began',
  caught: 'FOUND!',
  timeup: 'Time up',
};

function Detail({ entry, onBack }: { entry: HistoryEntry; onBack: () => void }) {
  const [gifExpired, setGifExpired] = useState(false);
  const t0 = entry.events?.[0]?.t ?? 0;
  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="eyebrow">
          {entry.code} · round {entry.round + 1} · you {entry.role === 'hider' ? 'hid' : 'sought'}
        </div>
        <button className="btn btn--ghost" style={{ width: 'auto' }} data-testid="history-back" onClick={onBack}>
          Back
        </button>
      </div>
      <div className="score-h">
        {entry.outcome === 'caught' ? 'Found' : 'Survived'} <em>{formatClock(entry.finalMin * 60_000)}.</em>
      </div>
      <div className="replay-frame" data-testid="history-replay">
        {entry.gifUrl && !gifExpired ? (
          <img src={entry.gifUrl} alt="Round replay" data-testid="history-replay-img" onError={() => setGifExpired(true)} />
        ) : (
          <span className="replay-pending" data-testid="history-replay-expired">
            {entry.gifUrl ? 'Replay expired on the server.' : 'No replay was rendered.'}
          </span>
        )}
      </div>
      <div className="panel section-gap" data-testid="history-timeline">
        <div className="field__label">What happened</div>
        {(entry.events ?? []).length === 0 && <div className="ht-prose">The event log is gone with the replay.</div>}
        {(entry.events ?? []).map((e, i) => (
          <div className="score-row" key={`${e.kind}:${e.t}:${i}`} data-testid="history-event">
            <span className="k">+{formatClock(Math.max(0, e.t - t0))}</span>
            <span className="v">
              {KIND_LABEL[e.kind] ?? e.kind}
              {e.label ? ` · ${e.label}` : ''}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

export function History({ onClose }: { onClose: () => void }) {
  const entries = useHistoryEntries();
  const [openId, setOpenId] = useState<string | null>(null);
  const open = entries.find((e) => e.id === openId) ?? null;

  return (
    <div className="screen screen--overlay" data-testid="history">
      <Contours opacity={0.08} />
      <div className="screen__inner">
        {open ? (
          <Detail entry={open} onBack={() => setOpenId(null)} />
        ) : (
          <>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <div className="eyebrow">Field record</div>
                <h1 className="howto__title">
                  Your <em>games.</em>
                </h1>
              </div>
              <button className="btn btn--ghost" data-testid="history-close" onClick={onClose} aria-label="Close history">
                Close
              </button>
            </div>
            {entries.length === 0 && (
              <p className="ht-prose" data-testid="history-empty">
                No finished rounds on this device yet. Go get found (or don&apos;t).
              </p>
            )}
            {entries.map((e) => (
              <button
                key={e.id}
                className="panel section-gap"
                style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
                data-testid={`history-row-${e.id}`}
                onClick={() => setOpenId(e.id)}
              >
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="field__label">
                    {new Date(e.endedAt).toLocaleDateString()} · {e.code} · round {e.round + 1}
                  </span>
                  <span className="field__label" style={{ color: e.outcome === 'caught' ? 'var(--signal)' : 'var(--moss)' }}>
                    {e.outcome === 'caught' ? 'FOUND' : 'SURVIVED'} · {formatClock(e.finalMin * 60_000)}
                  </span>
                </div>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export { historyStore };
